import { ensureFirebaseAuth } from "@/lib/firebase";
import { log } from "@/lib/logger";

/**
 * Requests go through this app's same-origin proxy by default, because the
 * HealNova backend restricts CORS to an allowlist of origins. Set
 * VITE_API_DIRECT=true once this app's domain is allowlisted to talk to the
 * backend directly.
 */
const DIRECT = import.meta.env["VITE_API_DIRECT"] === "true";
const DIRECT_BASE =
  (import.meta.env["VITE_API_BASE_URL"] as string | undefined) ?? "https://stgbackend.healnova.ai";

export const API_BASE = DIRECT ? `${DIRECT_BASE}/api` : "/api/proxy";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

const STATUS_FALLBACK: Record<number, string> = {
  400: "Some of the details you entered aren't valid.",
  401: "Your session expired. Please sign in again.",
  403: "Access forbidden. Please check your account permissions.",
  404: "We couldn't find what you were looking for.",
  409: "A doctor account already exists for this login.",
  422: "Some of the details you entered aren't valid.",
  429: "Too many requests. Please slow down and try again.",
  500: "The HealNova service is having trouble right now.",
  502: "Unable to reach the HealNova service.",
  503: "The HealNova service is temporarily unavailable.",
};

async function authHeader(forceRefresh = false): Promise<Record<string, string>> {
  const auth = await ensureFirebaseAuth();
  const user = auth?.currentUser;
  if (!user) return {};
  const token = await user.getIdToken(forceRefresh);
  return { Authorization: `Bearer ${token}` };
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  signal?: AbortSignal | undefined;
  /** Extra headers. When provided, Firebase auth headers are skipped. */
  headers?: Record<string, string> | undefined;
}

async function send<T>(path: string, options: RequestOptions, retry: boolean): Promise<T> {
  const { method = "GET", body, signal, headers: extraHeaders } = options;
  const done = log.start("api", "call", { method, path, ...(retry ? { retry: true } : {}) });

  // FormData (file uploads) must reach fetch untouched: JSON.stringify-ing it
  // serialises to "{}" (it has no enumerable own properties) and silently
  // drops the file, and a manually-set Content-Type loses the multipart
  // boundary the browser would otherwise generate.
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData;

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      signal: signal ?? null,
      headers: {
        ...(body !== undefined && !isFormData ? { "Content-Type": "application/json" } : {}),
        ...(extraHeaders ? extraHeaders : await authHeader(retry)),
      },
      body: body === undefined ? null : isFormData ? (body as FormData) : JSON.stringify(body),
    });
  } catch (error) {
    // Aborts are routine (unmounted component, superseded query) — not failures,
    // and their stacks are pure noise, so only the reason is kept.
    const aborted = signal?.aborted || (error as Error)?.name === "AbortError";
    if (aborted) done({ level: "debug", event: "call.aborted" });
    else done({ level: "error", event: "call.failed", error });
    throw error;
  }

  const raw = await response.text();
  let payload: unknown = null;
  try {
    payload = raw ? JSON.parse(raw) : null;
  } catch {
    payload = null;
  }

  if (!response.ok) {
    // A stale ID token is the most common 401 — refresh once and retry.
    if (response.status === 401 && !retry && !extraHeaders) {
      done({ level: "debug", event: "call.retry", status: 401 });
      return send<T>(path, options, true);
    }
    const message =
      (payload as { message?: string; error?: string } | null)?.message ??
      (payload as { error?: string } | null)?.error ??
      STATUS_FALLBACK[response.status] ??
      "Something went wrong. Please try again.";
    done({
      level: response.status >= 500 ? "error" : "warn",
      status: response.status,
      msg: message,
    });
    throw new ApiError(response.status, message);
  }

  done({ status: response.status });
  return payload as T;
}

export const apiClient = {
  get: <T>(path: string, signal?: AbortSignal) => send<T>(path, { signal }, false),
  post: <T>(path: string, body?: unknown) => send<T>(path, { method: "POST", body }, false),
  put: <T>(path: string, body?: unknown) => send<T>(path, { method: "PUT", body }, false),
  del: <T>(path: string, body?: unknown) => send<T>(path, { method: "DELETE", body }, false),
  /** Escape hatch for callers with their own auth scheme (admin x-admin-secret). */
  request: <T>(path: string, options: RequestOptions) => send<T>(path, options, false),
};
